import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Package,
  TrendingUp,
  Users,
  Truck,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign
} from 'lucide-react';
import { Card, CardContent } from '../ui';
import { formatCurrency } from '../../lib/utils';

const gradientConfigs = {
  ordered: { bg: 'from-blue-500 to-blue-600', icon: Clock },
  at_carrier: { bg: 'from-orange-400 to-orange-600', icon: Truck },
  shipped: { bg: 'from-yellow-400 to-amber-500', icon: Package },
  arrived: { bg: 'from-purple-400 to-purple-600', icon: Package },
  at_home: { bg: 'from-teal-400 to-teal-600', icon: Package },
  delivered_to_client: { bg: 'from-emerald-400 to-emerald-600', icon: CheckCircle },
  profit: { bg: 'from-emerald-500 to-green-600', icon: TrendingUp },
  unpaid: { bg: 'from-red-400 to-red-600', icon: AlertCircle },
  debt: { bg: 'from-orange-500 to-red-500', icon: DollarSign },
  accounts: { bg: 'from-indigo-400 to-indigo-600', icon: Users }
};

const DashboardStats = React.memo(function DashboardStats({ stats, accountLimits, paymentStats }) {
  const [hoveredCard, setHoveredCard] = useState(null);

  const statCards = useMemo(() => [
    {
      title: 'გამოწერილია',
      value: stats?.total_ordered || 0,
      key: 'ordered'
    },
    {
      title: 'გადამზიდავში',
      value: stats?.total_at_carrier || 0,
      key: 'at_carrier'
    },
    {
      title: 'გამოგზავნილია',
      value: stats?.total_shipped || 0,
      key: 'shipped'
    },
    {
      title: 'ჩამოტანილია',
      value: stats?.total_arrived || 0,
      key: 'arrived'
    },
    {
      title: 'სახლში/საწყობში',
      value: stats?.total_at_home || 0,
      key: 'at_home'
    },
    {
      title: 'გაცემული',
      value: stats?.total_delivered_to_client || 0,
      key: 'delivered_to_client'
    },
    {
      title: 'მოგება',
      value: formatCurrency(stats?.total_profit_gel || 0),
      key: 'profit',
      isCurrency: true
    },
    {
      title: 'გადაუხდელი',
      value: formatCurrency(paymentStats?.total_unpaid || 0),
      key: 'unpaid',
      isCurrency: true
    },
    {
      title: 'ვალიანი ნივთები',
      value: paymentStats?.items_with_debt || 0,
      key: 'debt'
    },
    {
      title: 'აქაუნთები',
      value: accountLimits?.length || 0,
      key: 'accounts'
    }
  ], [stats, accountLimits, paymentStats]);

  const IconComponent = ({ config, className }) => {
    const Icon = config.icon;
    return <Icon className={className} />;
  };

  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5 mb-6 stagger-children">
      {statCards.map((stat) => {
        const config = gradientConfigs[stat.key];
        const isHovered = hoveredCard === stat.key;
        
        return (
          <div
            key={stat.key}
            onMouseEnter={() => setHoveredCard(stat.key)}
            onMouseLeave={() => setHoveredCard(null)}
            className={`
              relative overflow-hidden rounded-2xl p-4 cursor-pointer
              bg-gradient-to-br ${config.bg}
              text-white shadow-lg
              transition-all duration-300 ease-out
              ${isHovered ? 'transform scale-105 shadow-xl' : 'hover:shadow-xl'}
            `}
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10 transition-transform duration-300" />
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8" />
            
            <div className="relative z-10 flex flex-col justify-between h-full min-h-[80px]">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white/90">
                  {stat.title}
                </span>
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <IconComponent config={config} className="h-5 w-5 text-white" />
                </div>
              </div>
              
              <div className="mt-2">
                <div className={`text-2xl font-bold tracking-tight ${stat.isCurrency ? 'text-lg' : ''}`}>
                  {stat.value}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

DashboardStats.propTypes = {
  stats: PropTypes.shape({
    total_ordered: PropTypes.number,
    total_at_carrier: PropTypes.number,
    total_shipped: PropTypes.number,
    total_arrived: PropTypes.number,
    total_at_home: PropTypes.number,
    total_delivered_to_client: PropTypes.number,
    total_profit_gel: PropTypes.number
  }),
  accountLimits: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ),
  paymentStats: PropTypes.shape({
    total_unpaid: PropTypes.number,
    items_with_debt: PropTypes.number
  })
};

export default DashboardStats;